import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Design, DesignStyle, StylePreset, UchiwaType } from '../types';

function generateId(): string {
  return `design_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

const DEFAULT_STYLE: DesignStyle = {
  preset: 'simple',
  fontFamily: 'Noto Sans JP',
  textColor: '#FF6B9D',
  backgroundColor: '#FFFFFF',
  secondaryColor: '#FFB5D1',
  fontSize: 120,
  textOrientation: 'vertical',
  effects: [],
};

function createNewDesign(overrides?: Partial<Design>): Design {
  const now = Date.now();
  return {
    id: generateId(),
    userId: null,
    createdAt: now,
    updatedAt: now,
    text: 'うちわ',
    style: { ...DEFAULT_STYLE },
    uchiwaType: 'regular',
    isPublic: false,
    aiGenerated: false,
    ...overrides,
  };
}

interface DesignState {
  current: Design;
  history: Design[];
  historyIndex: number;
  isDirty: boolean;

  // Actions
  setText(text: string): void;
  setPreset(preset: StylePreset): void;
  setTextColor(color: string): void;
  setBackgroundColor(color: string): void;
  setSecondaryColor(color: string): void;
  setFontSize(size: number): void;
  setFontFamily(family: string): void;
  setUchiwaType(type: UchiwaType): void;
  setTextOrientation(orientation: 'horizontal' | 'vertical'): void;
  updateStyle(updates: Partial<DesignStyle>): void;
  loadDesign(design: Design): void;
  newDesign(): void;
  undo(): void;
  redo(): void;
  markSaved(): void;
}

const MAX_HISTORY = 50;

export const useDesignStore = create<DesignState>()(
  immer((set) => ({
    current: createNewDesign(),
    history: [],
    historyIndex: -1,
    isDirty: false,

    setText: (text) =>
      set((state) => {
        pushHistory(state);
        state.current.text = text;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    setPreset: (preset) =>
      set((state) => {
        pushHistory(state);
        state.current.style.preset = preset;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    setTextColor: (color) =>
      set((state) => {
        pushHistory(state);
        state.current.style.textColor = color;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    setBackgroundColor: (color) =>
      set((state) => {
        pushHistory(state);
        state.current.style.backgroundColor = color;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    setSecondaryColor: (color) =>
      set((state) => {
        pushHistory(state);
        state.current.style.secondaryColor = color;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    setFontSize: (size) =>
      set((state) => {
        pushHistory(state);
        state.current.style.fontSize = size;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    setFontFamily: (family) =>
      set((state) => {
        pushHistory(state);
        state.current.style.fontFamily = family;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    setUchiwaType: (type) =>
      set((state) => {
        pushHistory(state);
        state.current.uchiwaType = type;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    setTextOrientation: (orientation) =>
      set((state) => {
        pushHistory(state);
        state.current.style.textOrientation = orientation;
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    updateStyle: (updates) =>
      set((state) => {
        pushHistory(state);
        Object.assign(state.current.style, updates);
        state.current.updatedAt = Date.now();
        state.isDirty = true;
      }),

    loadDesign: (design) =>
      set((state) => {
        state.current = design;
        state.history = [];
        state.historyIndex = -1;
        state.isDirty = false;
      }),

    newDesign: () =>
      set((state) => {
        state.current = createNewDesign();
        state.history = [];
        state.historyIndex = -1;
        state.isDirty = false;
      }),

    undo: () =>
      set((state) => {
        if (state.historyIndex < 0 || state.history.length === 0) return;
        const prev = state.history[state.historyIndex];
        if (!prev) return;
        state.current = prev;
        state.historyIndex -= 1;
        state.isDirty = true;
      }),

    redo: () => {
      // Simple undo/redo: redo not implemented in this phase
      console.warn('Redo not yet implemented');
    },

    markSaved: () =>
      set((state) => {
        state.isDirty = false;
      }),
  })),
);

function pushHistory(state: DesignState): void {
  const snapshot = JSON.parse(JSON.stringify(state.current)) as Design;
  state.history = state.history.slice(0, state.historyIndex + 1);
  state.history.push(snapshot);
  if (state.history.length > MAX_HISTORY) {
    state.history.shift();
  }
  state.historyIndex = state.history.length - 1;
}
